import { openDB, IDBPDatabase } from 'idb';
import { LRUCache } from 'lru-cache';
import { isDeviceMemoryLow } from './deviceUtils';

// Type definitions
export interface CacheOptions {
  maxAge?: number; // TTL in milliseconds
  maxSize?: number; // Max number of items
  storeInMemory?: boolean; // Whether to store in memory cache as well
  chunkSize?: number; // Size for chunking large data
}

export interface CacheItem<T> {
  value: T;
  expires: number; // timestamp
  lastAccessed: number; // timestamp for LRU
  metadata?: Record<string, any>; // Additional metadata
  chunks?: number; // Number of chunks if data was chunked
  compressionRatio?: number; // Compression ratio if compressed
}

// Options for loading cache items
export interface LoadOptions {
  loadChunks?: boolean; // Whether to load chunked data
  chunkIndex?: number; // Which chunk to load if chunked
}

// In-memory LRU cache with enhanced size-based eviction
const memoryCache = new LRUCache<string, any>({
  max: 100, // Maximum 100 items
  ttl: 1000 * 60 * 5, // 5 minutes
  updateAgeOnGet: true, // Update age on access
  sizeCalculation: (value) => {
    // Estimate size in bytes (rough approximation)
    return JSON.stringify(value).length;
  },
  maxSize: 5 * 1024 * 1024, // 5MB max cache size
});

// IndexedDB setup
const DB_NAME = 'ai_assistant_cache';
const DB_VERSION = 5; // Increment version to trigger schema upgrade
const EMBEDDING_STORE = 'embeddings';
const RESPONSE_STORE = 'responses';
const MESSAGES_STORE = 'messages';
const CACHE_STATS_STORE = 'cache_stats';
const CACHE_KEYS_STORE = 'cache_keys'; // Track keys for each store for faster invalidation
const CACHE_CHUNKS_STORE = 'cache_chunks'; // Store for chunked data
const CACHE_RELATIONSHIPS_STORE = 'cache_relationships'; // Store for content relationships

// Initialize the database with enhanced schema
export const initCacheDB = async (): Promise<IDBPDatabase> => {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, newVersion) {
      // Handle version changes and create stores as needed
      if (oldVersion < 1) {
        // Create initial stores if this is a fresh database
        if (!db.objectStoreNames.contains(EMBEDDING_STORE)) {
          const embeddingStore = db.createObjectStore(EMBEDDING_STORE, { keyPath: 'contentHash' });
          embeddingStore.createIndex('expires', 'expires', { unique: false });
        }
        
        // Response store
        if (!db.objectStoreNames.contains(RESPONSE_STORE)) {
          const responseStore = db.createObjectStore(RESPONSE_STORE, { keyPath: 'queryHash' });
          responseStore.createIndex('expires', 'expires', { unique: false });
          responseStore.createIndex('topic', 'topic', { unique: false });
        }
        
        // Messages store
        if (!db.objectStoreNames.contains(MESSAGES_STORE)) {
          const messagesStore = db.createObjectStore(MESSAGES_STORE, { keyPath: 'sessionId' });
          messagesStore.createIndex('updated', 'updated', { unique: false });
        }
      }
      
      // Add cache_stats store in version 2
      if (oldVersion < 2) {
        if (!db.objectStoreNames.contains(CACHE_STATS_STORE)) {
          const cacheStatsStore = db.createObjectStore(CACHE_STATS_STORE, { keyPath: 'key' });
          cacheStatsStore.createIndex('lastUpdated', 'lastUpdated', { unique: false });
        }
      }
      
      // Add cache_keys store in version 3
      if (oldVersion < 3) {
        if (!db.objectStoreNames.contains(CACHE_KEYS_STORE)) {
          const cacheKeysStore = db.createObjectStore(CACHE_KEYS_STORE, { keyPath: 'storeId' });
        }
      }
      
      // Add cache_chunks store in version 4
      if (oldVersion < 4) {
        if (!db.objectStoreNames.contains(CACHE_CHUNKS_STORE)) {
          const cacheChunksStore = db.createObjectStore(CACHE_CHUNKS_STORE, { keyPath: 'chunkId' });
          cacheChunksStore.createIndex('itemKey', 'itemKey', { unique: false });
          cacheChunksStore.createIndex('expires', 'expires', { unique: false });
        }
      }
      
      // Add cache_relationships store in version 5
      if (oldVersion < 5) {
        if (!db.objectStoreNames.contains(CACHE_RELATIONSHIPS_STORE)) {
          const cacheRelationshipsStore = db.createObjectStore(CACHE_RELATIONSHIPS_STORE, { keyPath: 'key' });
          cacheRelationshipsStore.createIndex('lastUpdated', 'lastUpdated', { unique: false });
        }
      }
    }
  });
};

// Get database connection
let dbPromise: Promise<IDBPDatabase> | null = null;
const getDB = (): Promise<IDBPDatabase> => {
  if (!dbPromise) {
    dbPromise = initCacheDB();
  }
  return dbPromise;
};

// Track cache keys for faster invalidation
const trackCacheKey = async (key: string, storeName: string): Promise<void> => {
  try {
    const db = await getDB();
    
    const storeId = `${storeName}_keys`;
    const tx = db.transaction(CACHE_KEYS_STORE, 'readwrite');
    const store = tx.objectStore(CACHE_KEYS_STORE);
    
    // Get existing keys array or create new one
    const existingEntry = await store.get(storeId);
    let keys: string[] = existingEntry?.keys || [];
    
    // Add new key if it doesn't exist
    if (!keys.includes(key)) {
      keys.push(key);
    }
    
    // Store updated keys array
    await store.put({ storeId, keys });
    await tx.done;
  } catch (error) {
    console.error('Error tracking cache key:', error);
  }
};

// Remove key from tracking
const untrackCacheKey = async (key: string, storeName: string): Promise<void> => {
  try {
    const db = await getDB();
    
    const storeId = `${storeName}_keys`;
    const tx = db.transaction(CACHE_KEYS_STORE, 'readwrite');
    const store = tx.objectStore(CACHE_KEYS_STORE);
    
    // Get existing keys array
    const existingEntry = await store.get(storeId);
    if (!existingEntry?.keys) return;
    
    // Remove key
    const updatedKeys = existingEntry.keys.filter((k: string) => k !== key);
    
    // Store updated keys array
    await store.put({ storeId, keys: updatedKeys });
    await tx.done;
  } catch (error) {
    console.error('Error untracking cache key:', error);
  }
};

// Get all keys for a store
export const getCacheKeys = async (storeName: string): Promise<string[]> => {
  try {
    const db = await getDB();
    
    const storeId = `${storeName}_keys`;
    const tx = db.transaction(CACHE_KEYS_STORE, 'readonly');
    const store = tx.objectStore(CACHE_KEYS_STORE);
    
    // Get keys array
    const entry = await store.get(storeId);
    await tx.done;
    
    return entry?.keys || [];
  } catch (error) {
    console.error('Error getting cache keys:', error);
    return [];
  }
};

// Enhanced function to get item from cache with progressive loading
export const getCachedItem = async <T>(
  key: string, 
  storeName: string,
  options: LoadOptions = {}
): Promise<T | null> => {
  try {
    // Check memory cache first for fastest access
    if (memoryCache.has(key)) {
      const item = memoryCache.get(key) as CacheItem<T>;
      if (item && item.expires > Date.now()) {
        return item.value;
      }
      // Remove if expired
      memoryCache.delete(key);
    }
    
    // Check IndexedDB if not in memory
    const db = await getDB();
    
    // Validate that the store exists before attempting to access it
    if (!db.objectStoreNames.contains(storeName)) {
      console.error(`Store ${storeName} does not exist in the database`);
      return null;
    }
    
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const item = await store.get(key) as CacheItem<T> | undefined;
    
    if (item && item.expires > Date.now()) {
      // Check if this is a chunked item that needs to be loaded progressively
      if (item.chunks && options.loadChunks) {
        // It's a chunked item, we need to load all chunks
        const chunkData = await loadChunkedData(key, item.chunks);
        if (chunkData !== null) {
          // Replace item.value with reconstructed data
          item.value = chunkData as T;
        }
      }
      
      // Add to memory cache if it's not too large (approx <100KB)
      const itemSize = JSON.stringify(item).length;
      if (itemSize < 100000) {
        memoryCache.set(key, item);
      }
      
      // Update last accessed time
      updateLastAccessed(key, storeName).catch(console.error);
      return item.value;
    }
    
    // Remove if expired
    if (item) {
      removeExpiredItem(key, storeName).catch(console.error);
    }
    
    return null;
  } catch (error) {
    console.error('Error retrieving from cache:', error);
    return null;
  }
};

// Load chunked data from cache
async function loadChunkedData(itemKey: string, chunkCount: number): Promise<any | null> {
  try {
    const db = await getDB();
    if (!db.objectStoreNames.contains(CACHE_CHUNKS_STORE)) return null;
    
    const chunks: any[] = [];
    
    // Load all chunks in sequence
    for (let i = 0; i < chunkCount; i++) {
      const chunkId = `${itemKey}_chunk_${i}`;
      const tx = db.transaction(CACHE_CHUNKS_STORE, 'readonly');
      const store = tx.objectStore(CACHE_CHUNKS_STORE);
      const chunk = await store.get(chunkId);
      
      if (!chunk || chunk.expires <= Date.now()) {
        console.error(`Missing or expired chunk ${i} for item ${itemKey}`);
        return null; // If any chunk is missing, the data is incomplete
      }
      
      chunks.push(chunk.data);
    }
    
    // Reconstruct the original data
    return reconstructData(chunks);
  } catch (error) {
    console.error('Error loading chunked data:', error);
    return null;
  }
}

// Reconstruct data from chunks
function reconstructData(chunks: any[]): any {
  try {
    // For simple JSON data, we can just concatenate and parse
    if (chunks.every(chunk => typeof chunk === 'string')) {
      const jsonString = chunks.join('');
      return JSON.parse(jsonString);
    }
    
    // For binary data like Uint8Array, we need to concatenate the arrays
    if (chunks.every(chunk => chunk instanceof Uint8Array)) {
      const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
      const result = new Uint8Array(totalLength);
      let offset = 0;
      for (const chunk of chunks) {
        result.set(chunk, offset);
        offset += chunk.length;
      }
      return result;
    }
    
    // For mixed data types, we need a custom reconstruction logic
    // This is a simplified example
    return chunks;
  } catch (error) {
    console.error('Error reconstructing data from chunks:', error);
    return null;
  }
}

// Split data into chunks for storage
function chunkifyData(data: any, chunkSize: number = 1024 * 1024): any[] {
  try {
    // For string data, chunk by characters
    if (typeof data === 'string') {
      const chunks: string[] = [];
      for (let i = 0; i < data.length; i += chunkSize) {
        chunks.push(data.substring(i, i + chunkSize));
      }
      return chunks;
    }
    
    // For Uint8Array or ArrayBuffer, chunk by bytes
    if (data instanceof Uint8Array) {
      const chunks: Uint8Array[] = [];
      for (let i = 0; i < data.length; i += chunkSize) {
        chunks.push(data.slice(i, i + chunkSize));
      }
      return chunks;
    }
    
    // For JSON data, stringify first then chunk
    const jsonString = JSON.stringify(data);
    const chunks: string[] = [];
    for (let i = 0; i < jsonString.length; i += chunkSize) {
      chunks.push(jsonString.substring(i, i + chunkSize));
    }
    return chunks;
  } catch (error) {
    console.error('Error chunking data:', error);
    return [data]; // Return as single chunk if chunking fails
  }
}

// Enhanced function to set item in cache with key tracking and chunking support
export const setCachedItem = async <T>(
  key: string, 
  value: T, 
  storeName: string, 
  options: CacheOptions = {}
): Promise<boolean> => {
  try {
    const expires = options.maxAge ? Date.now() + options.maxAge : Date.now() + (24 * 60 * 60 * 1000); // Default 24h
    
    // Determine if data needs to be chunked (for large items)
    const dataSize = JSON.stringify(value).length;
    const needsChunking = dataSize > 1024 * 1024; // Chunk if > 1MB
    const chunkSize = options.chunkSize || 1024 * 1024; // Default 1MB chunks
    
    const item: CacheItem<T> = {
      value: value as T,
      expires,
      lastAccessed: Date.now(),
    };
    
    // Handle chunking for large data
    if (needsChunking) {
      // Split data into chunks
      const chunks = chunkifyData(value, chunkSize);
      
      // Store chunks separately
      await storeDataChunks(key, chunks, expires);
      
      // Update item to reference chunks instead of containing the data
      item.chunks = chunks.length;
      // Store a reference or summary instead of the full value
      item.value = null as any;
    }
    
    // Check if the item is small enough for in-memory cache
    const itemSize = JSON.stringify(item).length;
    const storeInMemory = options.storeInMemory !== false && itemSize < 100000;
    
    // Set in memory cache if appropriate
    if (storeInMemory && !needsChunking) {
      memoryCache.set(key, item);
    }
    
    // Set in IndexedDB
    const db = await getDB();
    
    // Check if store exists
    if (!db.objectStoreNames.contains(storeName)) {
      console.error(`Store ${storeName} does not exist in the database`);
      return false;
    }
    
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    
    // For cache_stats store, we need a different format
    if (storeName === CACHE_STATS_STORE) {
      await store.put({
        key,
        ...item.value,
        lastUpdated: new Date()
      });
    } else {
      await store.put(item);
      // Track this key for faster invalidation
      await trackCacheKey(key, storeName);
    }
    
    await tx.done;
    
    return true;
  } catch (error) {
    console.error('Error setting cache item:', error);
    return false;
  }
};

// Store data chunks in the chunks store
async function storeDataChunks(itemKey: string, chunks: any[], expires: number): Promise<boolean> {
  try {
    const db = await getDB();
    
    // Check if chunks store exists
    if (!db.objectStoreNames.contains(CACHE_CHUNKS_STORE)) {
      console.error(`Store ${CACHE_CHUNKS_STORE} does not exist in the database`);
      return false;
    }
    
    const tx = db.transaction(CACHE_CHUNKS_STORE, 'readwrite');
    const store = tx.objectStore(CACHE_CHUNKS_STORE);
    
    // Store each chunk
    for (let i = 0; i < chunks.length; i++) {
      const chunkId = `${itemKey}_chunk_${i}`;
      await store.put({
        chunkId,
        itemKey,
        data: chunks[i],
        chunkIndex: i,
        totalChunks: chunks.length,
        expires
      });
    }
    
    await tx.done;
    return true;
  } catch (error) {
    console.error('Error storing data chunks:', error);
    return false;
  }
}

// Update last accessed time (for LRU in IndexedDB)
const updateLastAccessed = async (key: string, storeName: string): Promise<void> => {
  try {
    const db = await getDB();
    
    // Check if store exists
    if (!db.objectStoreNames.contains(storeName)) {
      console.error(`Store ${storeName} does not exist in the database`);
      return;
    }
    
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const item = await store.get(key);
    
    if (item) {
      item.lastAccessed = Date.now();
      await store.put(item);
    }
    
    await tx.done;
  } catch (error) {
    console.error('Error updating last accessed:', error);
  }
};

// Enhanced function to remove expired items with chunk cleanup
const removeExpiredItem = async (key: string, storeName: string): Promise<void> => {
  try {
    // Remove from memory cache
    memoryCache.delete(key);
    
    // Remove from IndexedDB
    const db = await getDB();
    
    // Check if store exists
    if (!db.objectStoreNames.contains(storeName)) {
      console.error(`Store ${storeName} does not exist in the database`);
      return;
    }
    
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    
    // Get the item first to check if it has chunks
    const item = await store.get(key);
    
    // Delete the item
    await store.delete(key);
    await tx.done;
    
    // If the item had chunks, delete those too
    if (item && item.chunks) {
      await removeDataChunks(key, item.chunks);
    }
    
    // Untrack the key
    await untrackCacheKey(key, storeName);
  } catch (error) {
    console.error('Error removing expired item:', error);
  }
};

// Remove data chunks for an item
async function removeDataChunks(itemKey: string, chunkCount: number): Promise<void> {
  try {
    const db = await getDB();
    if (!db.objectStoreNames.contains(CACHE_CHUNKS_STORE)) return;
    
    const tx = db.transaction(CACHE_CHUNKS_STORE, 'readwrite');
    const store = tx.objectStore(CACHE_CHUNKS_STORE);
    
    // Delete all chunks for this item
    for (let i = 0; i < chunkCount; i++) {
      const chunkId = `${itemKey}_chunk_${i}`;
      await store.delete(chunkId);
    }
    
    await tx.done;
  } catch (error) {
    console.error('Error removing data chunks:', error);
  }
}

// Enhanced function to clean up expired items with improved chunk handling
export const cleanupExpiredItems = async (storeName: string, specificKeys?: string[]): Promise<number> => {
  try {
    const db = await getDB();
    
    // Check if store exists
    if (!db.objectStoreNames.contains(storeName)) {
      console.error(`Store ${storeName} does not exist in the database`);
      return 0;
    }
    
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    
    let count = 0;
    
    // If specific keys provided, only check those
    if (specificKeys && specificKeys.length > 0) {
      for (const key of specificKeys) {
        try {
          const item = await store.get(key);
          if (!item || item.expires <= Date.now()) {
            // If the item has chunks, remember to clean those up too
            if (item && item.chunks) {
              await removeDataChunks(key, item.chunks);
            }
            
            await store.delete(key);
            await untrackCacheKey(key, storeName);
            count++;
          }
        } catch (err) {
          console.error(`Error processing key ${key}:`, err);
        }
      }
    } else {
      // Check if the store has the expires index
      if (!store.indexNames.contains('expires')) {
        console.warn(`Store ${storeName} does not have 'expires' index, using cursor-based cleanup`);
        
        // Use cursor-based approach
        let cursor = await store.openCursor();
        
        while (cursor) {
          if (cursor.value.expires <= Date.now()) {
            // If the item has chunks, remember to clean those up too
            if (cursor.value.chunks) {
              await removeDataChunks(cursor.key.toString(), cursor.value.chunks);
            }
            
            await cursor.delete();
            await untrackCacheKey(cursor.key.toString(), storeName);
            count++;
          }
          cursor = await cursor.continue();
        }
      } else {
        // Use index-based approach for better performance
        const expiryIndex = store.index('expires');
        
        let cursor = await expiryIndex.openCursor();
        
        while (cursor) {
          if (cursor.value.expires <= Date.now()) {
            // If the item has chunks, remember to clean those up too
            if (cursor.value.chunks) {
              await removeDataChunks(cursor.primaryKey.toString(), cursor.value.chunks);
            }
            
            await cursor.delete();
            await untrackCacheKey(cursor.primaryKey.toString(), storeName);
            count++;
          }
          cursor = await cursor.continue();
        }
      }
    }
    
    await tx.done;
    
    // Also clean up orphaned chunks if we're not cleaning up a specific store
    if (!storeName.startsWith('cache_chunks') && !specificKeys) {
      await cleanupOrphanedChunks();
    }
    
    return count;
  } catch (error) {
    console.error('Error cleaning up expired items:', error);
    return 0;
  }
};

// Clean up orphaned chunks (chunks whose parent items no longer exist)
async function cleanupOrphanedChunks(): Promise<number> {
  try {
    const db = await getDB();
    if (!db.objectStoreNames.contains(CACHE_CHUNKS_STORE)) return 0;
    
    const tx = db.transaction(CACHE_CHUNKS_STORE, 'readonly');
    const store = tx.objectStore(CACHE_CHUNKS_STORE);
    
    // Get all chunks, grouped by itemKey
    const chunksByItem: Record<string, string[]> = {};
    let cursor = await store.openCursor();
    
    while (cursor) {
      const chunk = cursor.value;
      if (!chunksByItem[chunk.itemKey]) {
        chunksByItem[chunk.itemKey] = [];
      }
      chunksByItem[chunk.itemKey].push(chunk.chunkId);
      cursor = await cursor.continue();
    }
    
    await tx.done;
    
    // Check each item to see if it still exists
    let cleanedCount = 0;
    
    for (const [itemKey, chunkIds] of Object.entries(chunksByItem)) {
      // Extract store name from the item key (assuming a naming convention)
      const storeName = itemKey.split(':')[0] || EMBEDDING_STORE;
      
      // Check if item exists
      const itemExists = await checkItemExists(itemKey, storeName);
      
      if (!itemExists) {
        // Item doesn't exist, clean up its chunks
        const deleteTx = db.transaction(CACHE_CHUNKS_STORE, 'readwrite');
        const deleteStore = deleteTx.objectStore(CACHE_CHUNKS_STORE);
        
        for (const chunkId of chunkIds) {
          await deleteStore.delete(chunkId);
          cleanedCount++;
        }
        
        await deleteTx.done;
      }
    }
    
    return cleanedCount;
  } catch (error) {
    console.error('Error cleaning up orphaned chunks:', error);
    return 0;
  }
}

// Check if an item exists in a store
async function checkItemExists(key: string, storeName: string): Promise<boolean> {
  try {
    const db = await getDB();
    if (!db.objectStoreNames.contains(storeName)) return false;
    
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const item = await store.get(key);
    await tx.done;
    
    return !!item;
  } catch (error) {
    console.error('Error checking if item exists:', error);
    return false;
  }
}

// Enhanced function to clear all caches with memory optimization
export const clearAllCaches = async (): Promise<boolean> => {
  try {
    // Clear memory cache
    memoryCache.clear();
    
    // Clear IndexedDB
    const db = await getDB();
    
    // Get all store names that exist
    const storeNames = Array.from(db.objectStoreNames);
    
    // Create a transaction for all existing stores
    if (storeNames.length > 0) {
      const tx = db.transaction(storeNames, 'readwrite');
      
      // Clear each store
      for (const storeName of storeNames) {
        await tx.objectStore(storeName).clear();
      }
      
      await tx.done;
    }
    
    return true;
  } catch (error) {
    console.error('Error clearing caches:', error);
    return false;
  }
};

// Get estimated cache size in bytes
export const getCacheStorageSize = async (): Promise<Record<string, number>> => {
  try {
    const db = await getDB();
    const storeNames = [EMBEDDING_STORE, RESPONSE_STORE, MESSAGES_STORE];
    const result: Record<string, number> = {};
    
    for (const storeName of storeNames) {
      if (!db.objectStoreNames.contains(storeName)) continue;
      
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const keys = await store.getAllKeys();
      
      let storeSize = 0;
      
      for (const key of keys) {
        const item = await store.get(key);
        if (item) {
          storeSize += JSON.stringify(item).length;
        }
      }
      
      result[storeName] = storeSize;
    }
    
    return result;
  } catch (error) {
    console.error('Error measuring cache size:', error);
    return {};
  }
};

// Track content relationships for sophisticated cache invalidation
export const trackContentRelationship = async (
  sourceKey: string,
  sourceType: string,
  relatedKeys: string[],
  relatedType: string
): Promise<boolean> => {
  try {
    const db = await getDB();
    
    // Check if the store exists before proceeding
    if (!db.objectStoreNames.contains(CACHE_RELATIONSHIPS_STORE)) {
      console.error(`Store ${CACHE_RELATIONSHIPS_STORE} does not exist in the database`);
      return false;
    }
    
    await setCachedItem(
      `${sourceKey}_${sourceType}_relationships`,
      { relatedKeys, relatedType, lastUpdated: new Date() },
      CACHE_RELATIONSHIPS_STORE,
      { maxAge: 30 * 24 * 60 * 60 * 1000 } // 30 days
    );
    return true;
  } catch (error) {
    console.error('Error tracking content relationship:', error);
    return false;
  }
};

// Get related content keys
export const getRelatedContentKeys = async (
  sourceKey: string,
  sourceType: string
): Promise<{relatedKeys: string[], relatedType: string} | null> => {
  try {
    const db = await getDB();
    
    // Check if the store exists before proceeding
    if (!db.objectStoreNames.contains(CACHE_RELATIONSHIPS_STORE)) {
      console.error(`Store ${CACHE_RELATIONSHIPS_STORE} does not exist in the database`);
      return null;
    }
    
    return await getCachedItem(
      `${sourceKey}_${sourceType}_relationships`, 
      CACHE_RELATIONSHIPS_STORE
    );
  } catch (error) {
    console.error('Error getting related content keys:', error);
    return null;
  }
};
